/**
 * SEO and JSON-LD for the public places map route (`/place/map`).
 * Colocated head builder pattern matches `place/list.seo.ts` and `contact.seo.ts`.
 */

import { env } from '@web/src/lib/env.client'
import { robotsMetaForEnvironment, seo } from '@web/src/lib/seo'
import { absoluteUrlForPath, baseUrl } from '@web/src/lib/utilsClient'

const MAP_PATH = '/place/map'

function buildPlaceMapBreadcrumbJsonLd(pageUrl: string): Record<string, unknown> {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: absoluteUrlForPath('/') },
      { '@type': 'ListItem', position: 2, name: 'Place Categories', item: absoluteUrlForPath('/place') },
      { '@type': 'ListItem', position: 3, name: 'Discover Places', item: absoluteUrlForPath('/place/list') },
      { '@type': 'ListItem', position: 4, name: 'Places map', item: pageUrl },
    ],
  }
}

function buildPlaceMapWebPageJsonLd(pageUrl: string, description: string): Record<string, unknown> {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: 'Places map',
    description,
    url: pageUrl,
    image: absoluteUrlForPath('/logo-og-image.webp'),
    isPartOf: {
      '@type': 'WebSite',
      name: 'Spicy Friends',
      url: absoluteUrlForPath('/'),
    },
  }
}

/** Loader shape reserved for future map head data (filters, counts). */
export type PlaceMapPageSeoLoaderInput = Record<string, never>

export function placeMapPageHeadFromLoaderData(_loaderData?: PlaceMapPageSeoLoaderInput | null | undefined) {
  const pageUrl = `${baseUrl()}${MAP_PATH}`
  const description =
    'Browse lifestyle clubs on an interactive map—pan, zoom, and filter by distance or tags. Switch to list view to compare venues and plan meetups.'

  const jsonLd = [buildPlaceMapWebPageJsonLd(pageUrl, description), buildPlaceMapBreadcrumbJsonLd(pageUrl)]

  return seo({
    title: `Discover places on the map | ${env.VITE_APP_NAME}`,
    description,
    url: pageUrl,
    image: '/logo-og-image.webp',
    robots: robotsMetaForEnvironment({
      production: 'index, follow',
      staging: 'noindex,nofollow',
    }),
    jsonLd,
  })
}
